import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, RefreshCw, Sparkles, Brain, Trophy, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * LessonQuiz — Quick 3-question multiple-choice quiz at end of every lesson.
 *
 * Props:
 *   lessonKey: "L<level>_<idx>"
 *   questions: [{ q, options[4], correct }]  (in current language)
 *   labels: i18n strings
 */
export const LessonQuiz = ({ lessonKey, questions, labels = {} }) => {
  const { completeLesson, user, progress } = useAuth();
  const isAuthed = user && user !== false;
  const isAlreadyDone = progress?.completed_keys?.includes(lessonKey);

  const [answers, setAnswers] = useState({}); // {qIdx: optionIdx}
  const [checked, setChecked] = useState(false);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when lesson changes
  useEffect(() => {
    setAnswers({});
    setChecked(false);
    setScore(0);
    setError(null);
  }, [lessonKey]);

  const allAnswered = questions.length > 0 &&
    Object.keys(answers).length === questions.length;

  const handleCheck = async () => {
    if (!allAnswered) return;
    let s = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correct) s += 1;
    });
    setScore(s);
    setChecked(true);

    // Submit to backend if authenticated AND not already completed AND passed
    if (isAuthed && !isAlreadyDone && s >= Math.max(2, questions.length - 1)) {
      setSubmitting(true);
      setError(null);
      try {
        await completeLesson(lessonKey, s, questions.length, Object.values(answers));
      } catch (e) {
        setError(e.message);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setChecked(false);
    setScore(0);
    setError(null);
  };

  const passMark = Math.max(2, questions.length - 1);
  const passed = checked && score >= passMark;
  const failed = checked && score < passMark;

  const interpolate = (str, vars) =>
    Object.keys(vars).reduce((s, k) => s.replaceAll(`{${k}}`, vars[k]), str);

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-purple-400/20 bg-gradient-to-br from-purple-950/30 to-zinc-950/60 p-6 backdrop-blur-md md:p-8" data-testid="lesson-quiz">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-purple-400/30 bg-purple-400/10">
          <Brain className="h-5 w-5 text-purple-300" />
        </div>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-300/80">
            {labels.quiz_title}
          </div>
          <p className="text-sm text-white/55">
            {labels.quiz_sub}
          </p>
        </div>
        {isAlreadyDone && (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300" data-testid="quiz-already-done-badge">
            <Trophy className="h-3 w-3" />
            {labels.lesson_done_badge}
          </span>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-5">
        {questions.map((q, qi) => (
          <div key={qi} className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
            <div className="mb-3 flex items-start gap-2">
              <span className="mt-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-purple-300/70">
                {labels.quiz_q} {qi + 1}
              </span>
            </div>
            <h4 className="mb-3 text-base font-bold text-white">
              {q.q}
            </h4>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {q.options.map((opt, oi) => {
                const picked = answers[qi] === oi;
                const isCorrect = oi === q.correct;
                const showRight = checked && isCorrect;
                const showWrong = checked && picked && !isCorrect;
                return (
                  <button
                    key={oi}
                    type="button"
                    onClick={() => !checked && setAnswers((a) => ({ ...a, [qi]: oi }))}
                    disabled={checked}
                    data-testid={`quiz-q${qi}-opt${oi}`}
                    className={`group relative flex items-start gap-3 rounded-lg border px-3.5 py-2.5 text-left text-sm transition-all ${
                      showRight
                        ? "border-emerald-400/60 bg-emerald-400/10 text-emerald-100 shadow-[0_0_18px_rgba(16,185,129,0.18)]"
                        : showWrong
                        ? "border-red-400/60 bg-red-400/10 text-red-100"
                        : picked
                        ? "border-purple-400/50 bg-purple-400/10 text-purple-100"
                        : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25 hover:bg-white/[0.05]"
                    } ${checked ? "cursor-default" : "cursor-pointer"}`}
                  >
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${
                      showRight
                        ? "border-emerald-400 bg-emerald-400 text-black"
                        : showWrong
                        ? "border-red-400 bg-red-400 text-black"
                        : picked
                        ? "border-purple-400 bg-purple-400 text-black"
                        : "border-white/30 text-white/40"
                    }`}>
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {showRight && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-300" />}
                    {showWrong && <XCircle className="h-4 w-4 shrink-0 text-red-300" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Result + Actions */}
      <div className="mt-6">
        <AnimatePresence mode="wait">
          {!checked && (
            <motion.button
              key="check"
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onClick={handleCheck}
              disabled={!allAnswered}
              data-testid="quiz-check-btn"
              className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-purple-500 to-fuchsia-600 px-5 py-3 text-xs font-black uppercase tracking-[0.25em] text-white shadow-[0_0_24px_rgba(168,85,247,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(168,85,247,0.55)] disabled:cursor-not-allowed disabled:from-white/10 disabled:to-white/10 disabled:text-white/40 disabled:shadow-none"
            >
              <Sparkles className="h-4 w-4" />
              {allAnswered ? labels.quiz_check : labels.quiz_unanswered}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </motion.button>
          )}

          {passed && (
            <motion.div
              key="pass"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-xl border border-emerald-400/40 bg-gradient-to-br from-emerald-400/15 to-emerald-500/5 p-5 text-center shadow-[0_0_40px_rgba(16,185,129,0.18)]"
              data-testid="quiz-passed"
            >
              <Trophy className="mx-auto mb-2 h-8 w-8 text-emerald-300" />
              <div className="font-display text-xl font-black tracking-tight text-emerald-100 md:text-2xl">
                {labels.quiz_passed}
              </div>
              <p className="mt-1 text-sm text-emerald-200/80">
                {interpolate(labels.quiz_passed_sub || "", { score, total: questions.length })}
              </p>
              {submitting && (
                <div className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-emerald-200/60">
                  <Loader2 className="h-3 w-3 animate-spin" /> saving…
                </div>
              )}
              {error && <div className="mt-2 text-xs text-red-300">{error}</div>}
            </motion.div>
          )}

          {failed && (
            <motion.div
              key="fail"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-stretch gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 p-5 text-center"
              data-testid="quiz-failed"
            >
              <div className="font-display text-lg font-black tracking-tight text-amber-200">
                {interpolate(labels.quiz_failed || "", { score, total: questions.length })}
              </div>
              <p className="text-sm text-amber-100/70">
                {labels.quiz_failed_sub}
              </p>
              <button
                type="button"
                onClick={handleRetry}
                data-testid="quiz-retry-btn"
                className="inline-flex items-center justify-center gap-2 self-center rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-amber-200 transition-all hover:border-amber-300 hover:bg-amber-400/20"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {labels.quiz_retry}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LessonQuiz;
